import type { Attributes, ExceptionContext, Span, SpanOptions } from '../types';
import { BaseLoggerProvider } from './base';

export interface DatadogProviderOptions {
  apiKey: string;
  service: string;
  env: string;
  site?: string;
  version?: string;
  requestId?: string;
  traceId?: string;
  parentSpanId?: string;
  sessionId?: string;
}

interface DatadogLogEntry {
  message: string;
  status: string;
  service: string;
  ddsource: string;
  ddtags: string;
  hostname: string;
  '@timestamp': string;
  'dd.trace_id'?: string;
  'dd.span_id'?: string;
  request_id?: string;
  [key: string]: unknown;
}

type DatadogStatus = 'debug' | 'info' | 'warn' | 'error' | 'critical';

function levelToStatus(level: string): DatadogStatus {
  switch (level) {
    case 'trace':
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    case 'warn':
      return 'warn';
    case 'error':
      return 'error';
    case 'fatal':
      return 'critical';
    default:
      return 'info';
  }
}

class DatadogSpan implements Span {
  private attributes: Attributes = {};

  setAttribute(key: string, value: unknown): void {
    this.attributes[key] = value;
  }

  setAttributes(attributes: Attributes): void {
    this.attributes = { ...this.attributes, ...attributes };
  }

  getAttributes(): Attributes {
    return this.attributes;
  }
}

export class DatadogProvider extends BaseLoggerProvider {
  private readonly apiKey: string;
  private readonly service: string;
  private readonly env: string;
  private readonly site: string;
  private readonly version: string | undefined;
  private readonly requestId: string | undefined;
  private readonly traceId: string | undefined;
  private readonly parentSpanId: string | undefined;
  private readonly sessionId: string | undefined;
  private userId: string | undefined;
  private readonly intakeUrl: string;
  private readonly ddtags: string;
  private buffer: DatadogLogEntry[] = [];

  constructor(options: DatadogProviderOptions) {
    super();
    this.apiKey = options.apiKey;
    this.service = options.service;
    this.env = options.env;
    this.site = options.site ?? 'us5.datadoghq.com';
    this.version = options.version;
    this.requestId = options.requestId;
    this.traceId = options.traceId;
    this.parentSpanId = options.parentSpanId;
    this.sessionId = options.sessionId;
    this.intakeUrl = `https://http-intake.logs.${this.site}/api/v2/logs`;
    this.ddtags = [
      `env:${this.env}`,
      this.version ? `version:${this.version}` : null,
    ]
      .filter(Boolean)
      .join(',');
  }

  private buildEntry(
    status: DatadogStatus,
    message: string,
    attributes?: Attributes
  ): DatadogLogEntry {
    const entry: DatadogLogEntry = {
      message,
      status,
      service: this.service,
      ddsource: 'cloudflare-worker',
      ddtags: this.ddtags,
      hostname: this.service,
      '@timestamp': new Date().toISOString(),
    };

    if (this.traceId) {
      entry['dd.trace_id'] = this.traceId;
    }
    if (this.parentSpanId) {
      entry['dd.span_id'] = this.parentSpanId;
    }
    if (this.requestId) {
      entry['request_id'] = this.requestId;
    }
    if (this.sessionId) {
      entry['rum_session_id'] = this.sessionId;
    }
    if (this.userId) {
      entry['usr.id'] = this.userId;
    }

    if (attributes) {
      for (const [k, v] of Object.entries(attributes)) {
        entry[k] = v;
      }
    }

    return entry;
  }

  private push(
    status: DatadogStatus,
    message: string,
    attributes?: Attributes
  ): void {
    this.buffer.push(this.buildEntry(status, message, attributes));
  }

  trace(message: string, attributes?: Attributes): void {
    this.push('debug', message, attributes);
  }

  debug(message: string, attributes?: Attributes): void {
    this.push('debug', message, attributes);
  }

  info(message: string, attributes?: Attributes): void {
    this.push('info', message, attributes);
  }

  warn(message: string, attributes?: Attributes): void {
    this.push('warn', message, attributes);
  }

  error(message: string, attributes?: Attributes): void {
    const err = attributes?.error;
    if (err instanceof Error) {
      const { error: _err, ...rest } = attributes ?? {};
      const entry = this.buildEntry('error', message, {
        'error.kind': err.constructor.name,
        'error.message': err.message,
        'error.stack': err.stack,
        ...rest,
      });
      entry.ddsource = 'nodejs';
      this.buffer.push(entry);
    } else {
      this.push('error', message, attributes);
    }
  }

  fatal(message: string, attributes?: Attributes): void {
    this.push('critical', message, attributes);
  }

  async startSpan<T>(
    name: string,
    options: SpanOptions,
    callback: (span: Span) => Promise<T>
  ): Promise<T> {
    const start = Date.now();
    const span = new DatadogSpan();

    if (options.attributes) {
      span.setAttributes(options.attributes);
    }

    try {
      const result = await callback(span);
      const duration = Date.now() - start;

      this.push('debug', name, {
        operation: name,
        op_type: options.op,
        duration_ms: duration,
        ...span.getAttributes(),
      });

      return result;
    } catch (error) {
      const duration = Date.now() - start;

      this.push('error', name, {
        operation: name,
        op_type: options.op,
        duration_ms: duration,
        ...span.getAttributes(),
      });

      throw error;
    }
  }

  captureException(error: Error, context?: ExceptionContext): void {
    const status = levelToStatus(context?.level ?? 'error');
    const entry = this.buildEntry(status, error.message, {
      'error.kind': error.constructor.name,
      'error.message': error.message,
      'error.stack': error.stack,
      ...context?.context,
    });
    entry.ddsource = 'nodejs';
    this.buffer.push(entry);
  }

  setUserId(id: string): void {
    this.userId = id;
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) {
      return;
    }

    const entries = this.buffer;
    this.buffer = [];

    try {
      await fetch(this.intakeUrl, {
        method: 'POST',
        headers: {
          'DD-API-KEY': this.apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entries),
      });
    } catch (err) {
      console.warn('[DatadogProvider] Failed to flush logs:', err);
    }
  }
}
